import keyBy from 'lodash.keyby';
import clonedeep from 'lodash.clonedeep';
import autoStrategyGroupSerializer from 'utils/optimization/bidmanager/autoStrategyGroupSerializer';
import manualStrategyGroupSerializer from 'utils/optimization/bidmanager/manualStrategyGroupSerializer';
import {STRATEGY_TYPES} from 'store/modules/bidmanager/utils/common';

import {
  defaultRule,
  defaultValuesForAction,
  OZON
} from 'store/modules/bidmanager/utils/utils';
import lodashGet from 'lodash.get';
import Utils from 'legacy/helpers/utils';
import groupBy from 'lodash.groupby';
import {COMPETITIVE_BIDDING_NO_OPTIMIZATION_AUTOSTRATEGY_IDS} from 'services/BidManagerAutostrategiesService';
import {initDefaultState, getRunOnceTime, getBiddableActions, DEFAULT_RUN_TYPE} from '..';


function prepareManualStrategyForSave (group) {
  const prepared = clonedeep(group);

  // add fallback rule to rules if it has action
  if (prepared.options.fallbackRule && prepared.options.fallbackRule.enabled) {
    prepared.options.rules.push(prepared.options.fallbackRule);
  }

  // crutch for task about the launch rate "every 15 minutes"
  if (prepared.time_of_day.length === 1 && typeof prepared.time_of_day[0] === 'string') {
    prepared.run_type = prepared.time_of_day[0];
    prepared.time_of_day = [0];
  } else {
    prepared.run_type = DEFAULT_RUN_TYPE;
  }

  return manualStrategyGroupSerializer(prepared);
}

function prepareAutoStrategyForSave (group) {
  return autoStrategyGroupSerializer(group);
}


export default {

  // 1 BidManagerGroupListPage
  currentBidmanagerGroup: state => state.currentBidmanagerGroup,
  isProgressMetricsLoading: state => state.isProgressMetricsLoading,
  placements: state => state.placements,
  groups: state => state.groups,
  groupsTotal: state => state.groupsTotal,
  currentPage: state => state.currentPage,
  perPage: state => state.perPage,
  progressData: state => state.progressData,
  progressDataMap: state => state.progressDataMap,
  sortingParams (state) {
    const res = state.sorting.reduce((acc, next) => {
      if (next.direction === 'desc') {
        return `${acc}-${next.name} `;
      }
      return `${acc}${next.name} `;
    }, '');
    return res.slice(0, res.length - 1);
  },
  profiles: state => state.profiles,
  goals: state => state.goals,

  // 2 Bidmanager
  getDefaultValuesForAction (state, getters) {
    return (action) => defaultValuesForAction({
      action,
      positionsChoices: state.positions,
      channelId: state.group.channel_id,
      placementType: getters.getCurrentBiddableChoice.placement_type,
      isManualStrategy: getters.isManualStrategy
    });
  },

  isManualStrategy: (state, getters) => Boolean(getters.strategyType) && getters.strategyType === STRATEGY_TYPES.MANUAL,

  getCurrentBiddableChoice (state, getters) {
    return getters.biddableTypesChoices.
      find(biddable => biddable.id === getters.biddableType) || null;
  },

  getCurrentPlacementChoice (state, getters) {
    return getters.getCurrentBiddableChoice?.placement_type || null;
  },

  canUseProgressMetrics: (state, getters, rootState, rootGetters) => rootGetters['user/canUser']('READ_COST_FACT'),

  canUseBidProgressMetrics (state, getters) {
    return getters.canUseProgressMetrics;
  },

  // returns group prepared to save
  _group_to_save (state, getters) {
    if (getters.strategyType === STRATEGY_TYPES.MANUAL) {
      return prepareManualStrategyForSave(state.group);
    }
    return prepareAutoStrategyForSave(state.group);
  },
  group: state => state.group,
  dirtyWithoutName (state) {
    // hack to get correct time_of_day
    const initState = {
      ...initDefaultState.group,
      time_of_day: getRunOnceTime()
    };

    const rhs = state.serverGroupState || initState;

    return !Utils.areEqual({...state.group, name: ''}, {...rhs, name: ''});
  },
  dirty (state, getters) {
    if (getters.dirtyWithoutName) {
      return true;
    }

    // hack to get correct time_of_day
    const initState = {
      ...initDefaultState.group,
      time_of_day: getRunOnceTime()
    };

    const rhs = state.serverGroupState || initState;
    return rhs.name !== state.group.name;
  },
  areRulesDefault (state, getters) {
    const rules = state.group.options.rules;

    // empty rules
    if (rules.length === 0) {
      return true;
    }

    // there is only one default rule
    if (rules.length > 1) {
      return false;
    }

    // check for default rule
    const defRule = defaultRule(getters.channelId, getters.biddable, 'uidtest');
    const curRule = {...rules[0], uid: 'uidtest'};
    return Utils.areEqual(defRule, curRule);
  },
  channelId: state => state.group.channel_id,
  biddable: state => state.group.options.biddable,
  rules: state => state.group.options.rules,
  fallbackRule: state => state.group.options.fallbackRule,
  notification: state => state.notification,
  runTypeChoices: state => state.runTypeChoices,
  biddableTypesChoices: state => state.biddableTypesChoices,
  enableOn: state => state.enableOn,
  enableOnChoices: state => state.enableOnChoices.map(item => ({
    ...item,
    name: item.name()
  })),

  formatDetailedPlacementName: (state, getters, rootState, rootGetters) => (placement) => {
    const placementTypeName = rootGetters['enums/getPlacementTypeById'](placement.placement_type)?.name || null;
    const strategyTypeName = rootGetters['enums/getPlacementStrategyTypeById'](placement.strategy_type)?.name || null;

    let formattedName = placement.name;

    if (placementTypeName) {
      formattedName += ` [${placementTypeName}]`;
    }

    if (strategyTypeName) {
      formattedName += ` [${strategyTypeName}]`;
    }

    return formattedName;
  },
  placementChoices: (state, getters, rootState, rootGetters) => state.placementChoices.filter(item =>

    // placement type is supported by BidManager
    rootGetters['placements/bidmanagerUnsupportedPlacementsTypes'].indexOf(item.placement_type) < 0 &&

    // placement should not be archived
    item.status !== 'ARCHIVED'
  ).map(placement => ({
    ...placement,
    ...{
      formattedName: getters.formatDetailedPlacementName(placement)
    }
  })),
  labelChoices: state => state.labelChoices,
  fieldsRawMapping (state) {
    const fields = state.fields.map(fieldGroup => fieldGroup.fields).flat();
    return keyBy(fields, 'id');
  },
  fields: (state) =>

    // get only available fields for current biddable
    state.fields.filter(group =>
      !group.fields[0].biddable || group.fields[0].biddable === state.group.options.biddable
    ).map(group => {
      if (group.fields.length < 1 || !group.fields[0].profile_id) {

        // common group
        return group;
      }

      /*
       * for webanalytics group, truncate all info about profile and goal from metric id
       * and store it inside `field` object
       */
      const fieldsMap = {};
      for (const field of group.fields) {
        if (field.goal_id) {

          // goal metric
          const prefix = `goal_${field.goal_id}_`;
          const field_id = field.id.replace(prefix, '');

          // metric already in map
          if (fieldsMap[field_id]) {
            fieldsMap[field_id].goals.push(field.goal_id);
          } else {
            const goals = [field.goal_id];
            const name = field.name.split('"')[0];
            fieldsMap[field_id] = {
              ...field, id: field_id, name, profile_id: undefined, goal_id: undefined, goals
            };
          }
        } else {

          // profile metric
          const prefix = `profile_${field.profile_id}_`;
          const field_id = field.id.replace(prefix, '');

          // metric already in map
          if (fieldsMap[field_id]) {
            fieldsMap[field_id].profiles.push(field.profile_id);
          } else {
            fieldsMap[field_id] = {
              ...field, id: field_id, profile_id: undefined, profiles: [field.profile_id]
            };
          }
        }
      }
      return {
        ...group,
        isWebanalytics: true,
        fields: Object.values(fieldsMap)
      };
    }),
  fieldsChoices (state, getters) {
    const choices = [];
    const biddable = getters.group.options.biddable;

    getters.fields.forEach((group) => {
      const children = [];
      group.fields.forEach((f) => {
        if (_.isUndefined(f.biddable) && !_.contains(f.exclude, biddable) ||
          f.biddable === biddable) {
          const cloned = _.clone(f);
          cloned.id = cloned.biddable_id || cloned.id;
          children.push(cloned);
        }
      });
      choices.push({
        id: group.group,
        name: group.group,
        icon: group.icon,
        children
      });
    });
    return choices;
  },
  fieldsMapping (state, getters) {
    const mapping = {};

    getters.fieldsChoices.forEach((g) => {
      g.children.forEach((f) => {
        mapping[f.id] = f;
      });
    });
    return mapping;
  },
  webanalyticsFieldsMap (state, getters) {
    const map = {};
    for (const group of getters.fields) {
      for (const field of group.fields) {
        if (group.isWebanalytics) {
          map[field.id] = field;
        }
      }
    }
    return map;
  },
  profilesGroupedByProvider (state, getters, rootState, rootGetters) {
    const providersMap = {};
    for (const provider of rootGetters['channels/providers']) {
      providersMap[provider.id] = {
        ...provider,
        children: []
      };
    }

    for (const profile of state.profiles) {
      providersMap[profile.provider_id].children.push({
        ...profile,
        name: `№${profile.xid} ${profile.name}`,
        icon: providersMap[profile.provider_id].icon
      });
    }
    return Object.values(providersMap);
  },
  goalsGroupedByProfiles (state, getters) {
    const profilesMap = {};
    for (const provider of getters.profilesGroupedByProvider) {
      for (const profile of provider.children) {
        profilesMap[profile.id] = {...profile, children: []};
      }
    }
    for (const goal of state.goals) {
      const profile = profilesMap[goal.profile_id];
      profile.children.push({
        ...goal,
        name: `№${goal.xid} ${goal.name}`,
        icon: profile.icon
      });
    }
    return Object.values(profilesMap);
  },
  goalsMap (state) {
    return keyBy(state.goals, 'id');
  },
  profilesMap (state) {
    return keyBy(state.profiles, 'id');
  },
  actionsForBiddable (state) {
    return (biddable) => getBiddableActions(state.actions, biddable);
  },
  actions (state) {
    return getBiddableActions(state.actions, state.group.options.biddable);
  },
  isLoggingAvailable: state => state.isLoggingAvailable,
  isProcessInactiveBiddablesAvailable: state => state.group.channel_id !== OZON() &&
    ['ADGROUP', 'AD', 'KEYWORD'].includes(state.group.options.biddable),
  dateframes: state => state.dateframes,
  types: state => state.types,
  operators: state => state.operators,
  positions: state => state.positions,
  isNew: state => state.isNew,
  isSelectableBiddableForPlacement: state => state.isSelectableBiddableForPlacement,
  biddableForDefaultName: state => state.biddableForDefaultName,
  errors: state => state.errors,
  validationRulesErrors: state => state.validationRulesErrors,
  errorByPath: state => path => lodashGet(state.errors, path),
  channelLoading: state => state.channelLoading,
  biddableLoading: state => state.biddableLoading,
  biddableType: state => state.group.options.biddable,
  progressMetrics (state, getters) {
    if (getters.biddable === 'PLACEMENT_URL') {
      return [];
    }
    return state.progressMetrics;
  },
  canUseAutostrategy (state, getters) {
    return getters.autostrategiesList.length > 0;
  },
  strategyType (state, getters) {
    if (state.strategyType) {
      return state.strategyType;
    }
    return getters.canUseAutostrategy ? null : STRATEGY_TYPES.MANUAL;
  },
  autostrategyId: state => state.group.autostrategy_id,
  autostrategiesList: (state, getters) => state.autostrategiesList.filter(i => {
    const isMatchCurrentChannelId = i.channel_ids.includes(state.group.channel_id);
    const isMatchCurrentBiddable = i.template.options.biddable === getters.biddable;

    return isMatchCurrentChannelId && isMatchCurrentBiddable;
  }),
  autostrategiesGroups (state, getters) {
    /*
     * Вариант «Конкурентных ставок» без оптимизации делит metaId и biddable с вариантом с оптимизацией,
     * поэтому в группировке ниже он перезаписал бы его. Пользователь выбирает его не в списке стратегий,
     * а переключателем в форме, так что в список выбора он не попадает.
     */
    const items = getters.autostrategiesList.filter(
      autostrategy => !COMPETITIVE_BIDDING_NO_OPTIMIZATION_AUTOSTRATEGY_IDS.includes(autostrategy.id)
    );

    const groups = groupBy(items, 'metaId');
    Object.keys(groups).forEach(g => {
      const groupItems = groups[g];
      groups[g] = keyBy(groupItems, 'template.options.biddable');
    });
    return groups;
  },
  autostrategy: (state, getters) =>
    getters.autostrategiesList.find(s => s.id === state.group.autostrategy_id),
  autostrategyArgumentsValues: state => state.group.autostrategy_arguments_values,
  autostrategyArguments: (state, getters) => getters.autostrategy?.arguments ? getters.autostrategy.arguments : [],
  autostrategyArgumentsMap: (state, getters) => keyBy(getters.autostrategyArguments, 'id'),
  populatedAutostrategyArgumentsSet (state, getters) {
    const populatedIds = getters.autostrategyArguments.
      map(argument => argument.populates).
      filter(argument => Boolean(argument));
    return new Set(populatedIds);
  },
  inputAutostrategyArguments: (state, getters) =>
    getters.autostrategyArguments.filter(
      argument => !getters.populatedAutostrategyArgumentsSet.has(argument.id)
    ),
  getRunOnceTime
};
